import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError } from '@repletoryreach/sdk-ui/notifications';
import { identity } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';

/** What the caller needs to show the operator once, and never again. */
export interface CreatedKey {
  name: string;
  prefix: string;
  key: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onCreated: (key: CreatedKey) => void;
}

/**
 * Issues a key for the platform organisation.
 *
 * Tenants mint their own keys in the tenant console, so this exists for the
 * platform's own integrations: a partner script, an internal service, a
 * support tool. The drawer hands the plaintext key to its caller rather than
 * rendering it, because the caller shows it in a dialog that cannot be
 * dismissed by accident.
 */
export function CreateKeyDrawer({ opened, onClose, onCreated }: Props) {
  const t = useT();
  const qc = useQueryClient();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setName('');
    setScopes('');
    setNameError(null);
  }, [opened]);

  const create = useMutation({
    mutationFn: (req: identity.CreateApiKeyRequest) => identity.createApiKey(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] })
  });

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('settings.apiKeys.create.errors.nameRequired'));
      return;
    }
    setNameError(null);
    const list = scopes.split(',').map((s) => s.trim()).filter(Boolean);
    try {
      const res = await create.mutateAsync({ name: trimmed, scopes: list.length ? list : null });
      onCreated({ name: res.data.name, prefix: res.data.prefix, key: res.data.key });
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.apiKeys.create.failedTitle'));
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('settings.apiKeys.create.title')}
      position="right"
      size="md"
    >
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('settings.apiKeys.create.intro', { org: user?.org.name ?? '' })}
        </Text>

        <TextInput
          label={t('common.name')}
          description={t('settings.apiKeys.create.nameHint')}
          value={name}
          onChange={(e) => { setName(e.currentTarget.value); if (nameError) setNameError(null); }}
          error={nameError ?? undefined}
          required
        />

        <TextInput
          label={t('settings.apiKeys.filters.scope')}
          description={t('settings.apiKeys.create.scopesHint')}
          value={scopes}
          onChange={(e) => setScopes(e.currentTarget.value)}
        />

        <Alert color="warning">{t('settings.apiKeys.create.oneSightNotice')}</Alert>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={submit} loading={create.isPending}>{t('settings.apiKeys.create.submit')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
